import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { projectsInfiniteQuery } from '@/entities/project';
import { useDebounce } from '@/shared/hooks/useDebounce/useDebounce';

/**
 * Custom hook for project picker with infinite scroll and server-side search.
 *
 * Provides all shared logic for project selection popovers:
 * - Infinite scroll pagination
 * - Debounced server-side search
 * - Loading states
 * - Popover open/close handling
 *
 * Used by StartBriefPopover and SaveToProjectPopover.
 *
 * @param workspaceId - The workspace to fetch projects from
 * @param includePermissions - Whether to resolve per-row permissions (gates the query on open)
 * @param externalOpen - When a consumer owns open state itself (e.g. opening the picker from an
 *   unrelated trigger), pass it here so the query and scroll effect key off the same resolved
 *   open value the caller renders with, instead of only the hook's own internal state.
 * @returns Object containing projects, loading states, and handlers
 */
export function useProjectPicker(
  workspaceId: string | null | undefined,
  includePermissions = false,
  externalOpen?: boolean,
) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const sentinelRef = useRef<HTMLDivElement>(null);

  const resolvedOpen = externalOpen ?? open;

  // Debounce search query to prevent query key changes on every keystroke
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Each permission-bearing row costs the authority one round-trip, so the gated
  // query runs only while the dropdown is open. Gating `enabled` rather than the
  // query key keeps the resolved rows in place through the exit animation and
  // stops list invalidations from re-resolving permissions for a closed picker.
  const { data, isPending, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    ...projectsInfiniteQuery(workspaceId, {
      sortBy: 'title',
      sortOrder: 'ASC',
      search: debouncedSearchQuery.trim() || undefined, // Server-side search by title
      includePermissions: includePermissions || undefined,
    }),
    enabled: !!workspaceId && (!includePermissions || resolvedOpen),
  });

  // Flatten all pages into a single array
  const projects = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);

  // Infinite scroll with IntersectionObserver
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !resolvedOpen) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { rootMargin: '100px' },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, fetchNextPage, isFetchingNextPage, resolvedOpen]);

  // Handle popover open/close
  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);

    // Reset search when closing
    if (!isOpen) {
      setSearchQuery('');
    }
  };

  return {
    // State. `open` is the resolved value (the external override when one is given,
    // otherwise the hook's own state), so every consumer reads a single source of truth.
    open: resolvedOpen,
    setOpen,
    searchQuery,
    setSearchQuery,

    // Data
    projects,
    isLoading: isPending,
    isFetchingNextPage,
    hasNextPage,

    // Refs
    sentinelRef,

    // Handlers
    handleOpenChange,
  };
}
